import { ActionIcon, Input, SegmentedControl, TextInput, Tooltip } from '@mantine/core';
import { IconClock } from '@tabler/icons-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './DrillRecords.module.css';
import { normalizeTime, shipTimeNow } from './record-view.js';

/**
 * Execution Wizard alanları (ADR 0128 Faz 3 Ek): gemi saatiyle `HH:MM` (dokunmatikte "şimdi"
 * düğmesi), `m:ss` süre ve üç durumlu (cevapsız / evet / hayır) seçim. Dokunma hedefleri en az
 * 44 px.
 */

export function TimeField({
  label,
  value,
  onChange,
  tzOffsetMin,
  readOnly,
  error,
  required,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  tzOffsetMin: number;
  readOnly?: boolean | undefined;
  error?: string | undefined;
  required?: boolean | undefined;
}) {
  const { t } = useTranslation();
  return (
    <div className={classes.timeRow}>
      <TextInput
        label={label}
        placeholder="HH:MM"
        inputMode="numeric"
        maxLength={5}
        size="md"
        required={required ?? false}
        readOnly={readOnly ?? false}
        value={value}
        error={error}
        onChange={(event) => onChange(event.currentTarget.value)}
        onBlur={(event) => onChange(normalizeTime(event.currentTarget.value))}
      />
      {readOnly ? null : (
        <Tooltip label={t('drillRecord.fields.now')}>
          <ActionIcon
            variant="light"
            size={42}
            aria-label={`${label}: ${t('drillRecord.fields.now')}`}
            onClick={() => onChange(shipTimeNow(new Date(), tzOffsetMin))}
          >
            <IconClock size={20} aria-hidden />
          </ActionIcon>
        </Tooltip>
      )}
    </div>
  );
}

/** Evet / hayır; `null` cevapsız (gönderimde eksik sayılır). */
export function YesNo({
  label,
  value,
  onChange,
  readOnly,
  error,
}: {
  label: string;
  value: boolean | null;
  onChange: (value: boolean) => void;
  readOnly?: boolean | undefined;
  error?: string | undefined;
}) {
  const { t } = useTranslation();
  const id = useId();
  return (
    <Input.Wrapper label={label} id={id} error={error}>
      <SegmentedControl
        aria-labelledby={`${id}-label`}
        fullWidth
        size="md"
        readOnly={readOnly ?? false}
        value={value === null ? '' : value ? 'yes' : 'no'}
        onChange={(next) => onChange(next === 'yes')}
        data={[
          { value: 'yes', label: t('drillRecord.fields.yes') },
          { value: 'no', label: t('drillRecord.fields.no') },
        ]}
      />
    </Input.Wrapper>
  );
}

/** Kapalı listeden seçim (adım sonucu, katılım, derece); `null` cevapsız. */
export function Choice<T extends string>({
  label,
  value,
  options,
  onChange,
  readOnly,
  error,
  hideLabel,
}: {
  label: string;
  value: T | null;
  options: readonly { value: T; label: string }[];
  onChange: (value: T) => void;
  readOnly?: boolean | undefined;
  error?: string | undefined;
  hideLabel?: boolean;
}) {
  const id = useId();
  return (
    <Input.Wrapper
      label={hideLabel ? undefined : label}
      id={id}
      error={error}
      aria-label={hideLabel ? label : undefined}
    >
      <SegmentedControl
        {...(hideLabel ? { 'aria-label': label } : { 'aria-labelledby': `${id}-label` })}
        fullWidth
        size="md"
        readOnly={readOnly ?? false}
        value={value ?? ''}
        onChange={(next) => onChange(next as T)}
        data={options.map((o) => ({ value: o.value, label: o.label }))}
      />
    </Input.Wrapper>
  );
}
